import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { RxCross2 } from "react-icons/rx";
import { toast } from "react-toastify";
import { FindIndex } from "Utils/Common/CommonMethods";
import { assignFormValues } from "../../../Utils/Auth";
import useLang from "./../../hooks/useLanguage";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const ChooseFileSignature = (props: any) => {
  const { t } = useLang();
  const signCanvas = useRef(
    props?.sysytemFieldName + props?.controlId
  ) as React.MutableRefObject<any>;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const divElement = useRef<HTMLDivElement | null>(null);
  const [uploadedSignature, setUploadedSignature] = useState<string>(
    props?.defaultValue?.includes("base64,") ? props.defaultValue : ""
  );

  // Draw the base64 image directly onto the canvas. We size the drawing buffer
  // to the canvas' real display size at draw time, because this control lives
  // inside a `d-none` dependency block and mounts with offsetWidth = 0, which
  // otherwise makes signature_pad's fromDataURL draw at zero width (invisible).
  const drawSignature = (base64: string) => {
    const canvasEl: HTMLCanvasElement | undefined =
      signCanvas?.current?.getCanvas?.();
    if (!canvasEl) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const displayWidth = canvasEl.offsetWidth || 300;
    const displayHeight = canvasEl.offsetHeight || 200;

    canvasEl.width = displayWidth * ratio;
    canvasEl.height = displayHeight * ratio;

    const ctx = canvasEl.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, displayWidth, displayHeight);

    const img = new Image();
    img.onload = () => {
      // Fit within the pad while preserving the image's aspect ratio.
      const scale = Math.min(
        displayWidth / img.width,
        displayHeight / img.height
      );
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.clearRect(0, 0, displayWidth, displayHeight);
      ctx.drawImage(img, (displayWidth - w) / 2, (displayHeight - h) / 2, w, h);
    };
    img.src = base64;
  };

  const renderToPad = (base64: string) => {
    if (!base64) {
      signCanvas?.current?.clear();
      return;
    }
    // Defer so the canvas is laid out (non-zero width) before we size/draw.
    setTimeout(() => drawSignature(base64));
  };

  useEffect(() => {
    if (props?.defaultValue?.includes("base64,")) {
      setUploadedSignature(props.defaultValue);
      renderToPad(props.defaultValue);
    } else {
      signCanvas?.current?.clear();
    }
  }, [props?.defaultValue, props?.padValue]);

  // Safety net: if we have a signature but the canvas ended up blank (e.g. the
  // block became visible after mounting hidden), redraw it.
  useEffect(() => {
    if (uploadedSignature) {
      const timer = setTimeout(() => drawSignature(uploadedSignature));
      return () => clearTimeout(timer);
    }
  }, [uploadedSignature]);

  // Scroll into view when a validation error is focused (mirrors SignPad behaviour).
  useEffect(() => {
    if (props.error && divElement.current) {
      divElement.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    props.setErrorFocussedInput && props.setErrorFocussedInput();
  }, [props?.errorFocussedInput]);

  const saveToPayload = async (base64: string) => {
    const newInputs = await assignFormValues(
      props.Inputs,
      props.index,
      props.depControlIndex,
      props.fieldIndex,
      base64,
      props.isDependency,
      props.repeatFieldSection,
      props.isDependencyRepeatFields,
      props.repeatFieldIndex,
      props.repeatDependencySectionIndex,
      props.repeatDepFieldIndex,
      undefined,
      props?.setInputs
    );

    if (props?.ArrayReqId) {
      const infectiousDataCopy = JSON?.parse(
        JSON?.stringify(props?.infectiousData)
      );
      infectiousDataCopy[
        FindIndex(props?.infectiousData, props?.ArrayReqId)
      ].sections = newInputs;
      props?.setInfectiousData([...infectiousDataCopy]);
    } else {
      props?.setInputs(newInputs);
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = e.target;
    const file = fileInput.files?.[0];
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (file && allowedTypes.includes(file.type)) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t("File size should not exceed 10 MB."));
        fileInput.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result as string;
        // Setting state triggers the draw effect; also draw immediately.
        setUploadedSignature(base64);
        drawSignature(base64);
        await saveToPayload(base64);
        props.fields && (props.fields.enableRule = "");
      };
      reader.readAsDataURL(file);
    } else {
      toast.error(
        t("Invalid file type or size. Please upload a valid image file.")
      );
    }

    // Reset the file input to allow re-uploading the same file.
    fileInput.value = "";
  };

  const clear = async () => {
    const canvasEl: HTMLCanvasElement | undefined =
      signCanvas?.current?.getCanvas?.();
    if (canvasEl) {
      canvasEl
        .getContext("2d")
        ?.clearRect(0, 0, canvasEl.width, canvasEl.height);
    }
    signCanvas?.current?.clear();
    setUploadedSignature("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    await saveToPayload("");
  };

  const inputId = `ChooseFileSignature_${props?.sysytemFieldName || ""}_${
    props?.controlId || ""
  }`;

  return (
    <>
      <div className="d-flex gap-2 mb-3 align-items-center">
        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          onChange={handleUpload}
          className="d-none"
          accept=".png,.jpeg,.jpg,image/png,image/jpeg"
          disabled={!!uploadedSignature}
        />
        <label
          htmlFor={uploadedSignature ? undefined : inputId}
          className={`dropzone pt-2 py-1 px-8 d-flex align-items-center ${
            uploadedSignature ? "opacity-50" : ""
          }`}
          style={{ cursor: uploadedSignature ? "not-allowed" : "pointer" }}
        >
          <div className="dz-message needsclick">
            <i className="bi bi-upload me-2"></i>
            {t("Upload Signature")}
          </div>
        </label>

        {uploadedSignature && (
          <button
            type="button"
            onClick={clear}
            title={t("Clear")}
            className="btn btn-icon btn-sm btn-light-danger"
          >
            <RxCross2 />
          </button>
        )}
      </div>

      <div className="overflow-hidden" ref={divElement}>
        <SignatureCanvas
          penColor="black"
          clearOnResize={false}
          canvasProps={{
            height: 200,
            className: "signatureCanvas",
            style: {
              backgroundColor: "#F3F6F9",
              width: "100%",
              pointerEvents: "none",
            },
            id: props.name,
          }}
          ref={signCanvas}
        />
      </div>

      {props.error && (
        <div className="form__error">
          <span>{t(props.error)}</span>
        </div>
      )}
    </>
  );
};

export default ChooseFileSignature;
